import { useCallback, useEffect, useState } from 'react';
import type { AirBlocker, EditProject } from '@airpost/shared';
import { ApiError, get, post } from '../api.js';
import { buildGraphicsPack } from '../edit-air.js';
import { t } from '../i18n.js';

/**
 * The render of an edit for air on the playout (ADR 074): the playout renders it from its masters, the page draws
 * only the graphics and follows the job. While a job runs the page asks how it goes every two seconds; once the
 * playout's library holds the clip, the story plays it.
 */

export type AirStatus = 'SENDING' | 'PENDING' | 'QUEUED' | 'RUNNING' | 'DONE' | 'FAILED' | 'CANCELLED';
export interface AirState {
  jobId: string | null;
  status: AirStatus;
  progress: number;
  error: string | null;
  revision: number;
  filename: string | null;
  mediaId: string | null;
  linked: { cueId: string } | null;
  stale: boolean;
  by: string | null;
  at: string;
}
export interface AirView {
  available: boolean;
  blockers: AirBlocker[];
  graphics: boolean;
  durationMs: number;
  air: AirState | null;
}
const ACTIVE: AirStatus[] = ['SENDING', 'PENDING', 'QUEUED', 'RUNNING'];

/** The edit's render for air, asked again every two seconds while one is under way. */
export function useAir(editId: string) {
  const [view, setView] = useState<AirView | null>(null);
  const refresh = useCallback(
    () =>
      get<AirView>(`/api/edits/${editId}/air`).then(
        (v) => setView(v),
        // the next poll tries again
        () => undefined,
      ),
    [editId],
  );
  useEffect(() => {
    let on = true;
    get<AirView>(`/api/edits/${editId}/air`).then(
      (v) => on && setView(v),
      () => undefined,
    );
    return () => {
      on = false;
    };
  }, [editId]);
  const active = !!view?.air && (ACTIVE.includes(view.air.status) || (view.air.status === 'DONE' && !view.air.linked && !view.air.stale));
  useEffect(() => {
    if (!active) return;
    const h = window.setInterval(() => void refresh(), 2000);
    return () => window.clearInterval(h);
  }, [active, refresh]);
  return { view, refresh, active };
}

const blockerText = (b: AirBlocker): string => {
  const what = t(`veAirBlock_${b.code}` as 'veAirBlock_look');
  const detail = b.detail ? ` (${b.detail})` : '';
  return b.piece === null ? `${what}${detail}` : `${t('veAirPiece')} ${b.piece + 1}: ${what}${detail}`;
};

const toBase64 = (bytes: Uint8Array): Promise<string> =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(r.error);
    r.onload = () => resolve(String(r.result).split(',')[1] ?? '');
    r.readAsDataURL(new Blob([bytes as BlobPart]));
  });

/** A line for the job's state, for the dialog and the button. */
export function airLine(a: AirState | null): string {
  if (!a) return '';
  switch (a.status) {
    case 'SENDING':
      return t('veAirSending');
    case 'PENDING':
      return t('veAirPending');
    case 'QUEUED':
      return t('veAirQueued');
    case 'RUNNING':
      return `${t('veAirRunning')} ${Math.round(a.progress * 100)}%`;
    case 'DONE':
      return t('veAirDone');
    case 'FAILED':
      return t('veAirFailed');
    case 'CANCELLED':
      return t('veAirCancelled');
  }
}

export function AirDialog({
  editId,
  view,
  refresh,
  canEdit,
  prepare,
  onClose,
}: {
  editId: string;
  view: AirView | null;
  refresh: () => Promise<void>;
  canEdit: boolean;
  /** Save what waits for the autosave and give the project as saved and its revision. */
  prepare: () => Promise<{ project: EditProject; revision: number }>;
  onClose: () => void;
}) {
  const [step, setStep] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const a = view?.air ?? null;
  const running = !!a && ACTIVE.includes(a.status);
  const blocked = !!view && view.blockers.length > 0;
  const start = async () => {
    setError(null);
    try {
      const { project, revision } = await prepare();
      let graphics: string | null = null;
      if (view?.graphics) {
        setStep(`${t('veAirGraphics')} 0%`);
        const pack = await buildGraphicsPack(project, (f) => setStep(`${t('veAirGraphics')} ${Math.round(f * 100)}%`));
        setStep(t('veAirSending'));
        graphics = await toBase64(pack);
      }
      setStep(t('veAirSending'));
      await post(`/api/edits/${editId}/air`, { revision, graphics });
      await refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setStep(null);
    }
  };
  const cancel = async () => {
    setError(null);
    try {
      await post(`/api/edits/${editId}/air/cancel`, {});
      await refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };
  return (
    <div className="ve-modal" role="dialog" aria-modal="true" onKeyDown={(e) => e.stopPropagation()}>
      <div className="ve-modal-box ve-air">
        <h3>📡 {t('veAirTitle')}</h3>
        <p className="ve-note">{t('veAirIntro')}</p>
        {!view ? (
          <p className="ve-note">…</p>
        ) : !view.available ? (
          <p className="ve-air-warn">{t('veAirNoPlayout')}</p>
        ) : (
          <>
            {blocked && (
              <div className="ve-air-warn">
                {t('veAirBlocked')}
                <ul>
                  {view.blockers.map((b, i) => (
                    <li key={i}>{blockerText(b)}</li>
                  ))}
                </ul>
              </div>
            )}
            {a && (
              <div className={`ve-air-state ${a.status.toLowerCase()}`} role="status">
                <b>{airLine(a)}</b>
                {a.status === 'RUNNING' && (
                  <span className="ve-air-bar">
                    <i style={{ width: `${Math.round(a.progress * 100)}%` }} />
                  </span>
                )}
                {a.status === 'DONE' && a.filename && <span className="ve-air-file">{a.filename}</span>}
                {a.status === 'DONE' && !a.stale && <span>{a.linked ? `✓ ${t('veAirLinked')}` : a.mediaId ? t('veAirNotLinked') : t('veAirIngesting')}</span>}
                {a.stale && <span className="ve-air-warn">{t('veAirStale')}</span>}
                {a.error && <span className="ve-air-error">{a.error}</span>}
                <small>
                  {a.by ?? ''} · {new Date(a.at).toLocaleString('el-GR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                </small>
              </div>
            )}
          </>
        )}
        {step && <p className="ve-air-step">{step}</p>}
        {error && <p className="ve-air-error">{error}</p>}
        <div className="ve-modal-actions">
          {running ? (
            <button type="button" className="btn" disabled={!canEdit} onClick={() => void cancel()}>
              ■ {t('veAirCancel')}
            </button>
          ) : (
            <button type="button" className="btn primary" disabled={!canEdit || !view?.available || blocked || !!step} onClick={() => void start()}>
              📡 {a?.status === 'DONE' ? t('veAirAgain') : t('veAirStart')}
            </button>
          )}
          <button type="button" className="btn" onClick={onClose}>
            {t('veAirClose')}
          </button>
        </div>
      </div>
    </div>
  );
}
